import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "ev-map-container" }
const _hoisted_2 = { class: "map-wrapper" }
const _hoisted_3 = { class: "map-controls" }
const _hoisted_4 = { class: "filters-section" }
const _hoisted_5 = { class: "status-filters" }
const _hoisted_6 = { class: "stats-section" }
const _hoisted_7 = { class: "stat-item" }
const _hoisted_8 = { class: "stat-value" }
const _hoisted_9 = { class: "stat-item" }
const _hoisted_10 = { class: "stat-value" }
const _hoisted_11 = { class: "actions-section" }
const _hoisted_12 = { class: "map-container" }
const _hoisted_13 = {
  key: 0,
  class: "map-loading"
}
const _hoisted_14 = {
  key: 0,
  class: "map-error"
}
const _hoisted_15 = {
  key: 1,
  class: "map-loading"
}
const _hoisted_16 = {
  key: 0,
  class: "vehicle-details"
}
const _hoisted_17 = { class: "detail-item" }
const _hoisted_18 = { class: "detail-item" }
const _hoisted_19 = { class: "detail-item" }
const _hoisted_20 = { class: "detail-item" }
const _hoisted_21 = { class: "detail-item" }
const _hoisted_22 = { class: "detail-item" }
const _hoisted_23 = { class: "detail-item" }
const _hoisted_24 = { class: "detail-item" }
const _hoisted_25 = {
  key: 0,
  class: "vehicle-images"
}
const _hoisted_26 = { class: "images-grid" }
const _hoisted_27 = { class: "image-slot" }

import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { PictureFilled } from '@element-plus/icons-vue'

import { EvMapCar } from '@/api/car-ev-data'
import { getEvMapCars, getEvCarProfiles } from '@/api/car-ev-data'
import { getEvAreas, EvArea } from '@/api/ev-areas'
import { routeNames } from '@/router/route-names'

import CommonError from '@/components/CommonError.vue'

import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

// -------------------- STATE --------------------

export default /*@__PURE__*/_defineComponent({
  __name: 'EVMap',
  setup(__props) {

const mapContainer = ref<HTMLDivElement>()
const leafletMap = ref<L.Map | null>(null)
const markerClusterGroup = ref<L.MarkerClusterGroup | null>(null)
const areasLayerGroup = ref<L.LayerGroup | null>(null)

const markers = ref<Map<string, L.Marker>>(new Map())
const evCars = ref<EvMapCar[]>([])
const evAreas = ref<EvArea[]>([])
const loading = ref(false)
const refreshing = ref(false)
const error = ref<string | null>(null)
const lastUpdated = ref(new Date())

const stats = ref<Record<string, number>>({})

const isMounted = ref(false)

const showVehicleDialog = ref(false)
const selectedVehicle = ref<EvMapCar | null>(null)

const selectedStatuses = ref<string[]>(['active', 'ongoing', 'reserved'])

const highlightedVehicleId = ref<string | null>(null)
const highlightedMarker = ref<L.Marker | null>(null)

const router = useRouter()

// -------------------- CONFIG --------------------
const vehicleStatuses = [
  { value: 'active', label: 'Available', color: '#67C23A' },
  { value: 'ongoing', label: 'Ongoing', color: '#E6A23C' },
  { value: 'reserved', label: 'Reserved', color: '#409EFF' },
  { value: 'inactive', label: 'Deactive', color: '#F56C6C' },
  { value: 'on_hold', label: 'On Hold', color: '#909399' },
]

const normalizeStatus = (s?: string): string => {
  const map: Record<string, string> = {
    available: 'active',
    deactive: 'inactive',
    ongoing: 'ongoing',
    reserved: 'reserved',
    on_hold: 'on_hold',
  }
  return s ? map[s] || s : 'unknown'
}

// -------------------- HELPERS --------------------
const filteredEvCars = computed(() => getVisibleVehicles())

const totalVehiclesCount = computed(() => {
  if (Object.keys(stats.value).length > 0) {
    return Object.values(stats.value).reduce((a, b) => a + Number(b), 0)
  }
  return evCars.value?.length || 0
})

const getVisibleVehicles = () => {
  return (evCars.value || [])
    .filter(v => {
      if (!v?.location) return false
      const lat = Number(v.location.lat)
      const lng = Number(v.location.lng)
      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180
      )
    })
    .filter(v => {
      const norm = normalizeStatus(v.booking_status || v.status)
      return (
        !selectedStatuses.value.length || selectedStatuses.value.includes(norm)
      )
    })
}

const getCountByStatus = (status: string): number => {
  const statusMap: Record<string, string> = {
    active: 'available',
    ongoing: 'ongoing',
    reserved: 'reserved',
    inactive: 'deactive',
    on_hold: 'on_hold',
  }
  const apiStatus = statusMap[status] || status
  if (stats.value[apiStatus] !== undefined) return stats.value[apiStatus]

  const visibleVehicles = getVisibleVehicles()
  return visibleVehicles.filter(car => {
    const carStatus = car.booking_status || car.status
    return carStatus === status
  }).length
}

const getStatusColor = (status: string): string =>
  vehicleStatuses.find(s => s.value === status)?.color || '#909399'

const getStatusLabel = (status: string): string => {
  const statusConfig = vehicleStatuses.find(s => s.value === status)
  return statusConfig?.label || status
}

const getStatusType = (status: string): string => {
  switch (status) {
    case 'active':
      return 'success'
    case 'ongoing':
      return 'warning'
    case 'reserved':
      return 'primary'
    case 'inactive':
      return 'danger'
    default:
      return 'info'
  }
}

const formatTime = (date: Date): string => dayjs(date).format('HH:mm:ss')

const createHighlightedMarkerIcon = (): L.DivIcon =>
  L.divIcon({
    className: 'ev-marker-highlighted',
    html: `<span style="
      display:inline-block;width:24px;height:24px;border-radius:50%;
      background:#ff6b6b;box-shadow:0 0 0 4px #fff inset, 0 0 0 2px rgba(255,107,107,0.8), 0 0 20px rgba(255,107,107,0.6);
      animation: pulse 2s infinite;
    "></span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })

const createMarkerIcon = (bookingStatus: string): L.DivIcon =>
  L.divIcon({
    className: 'ev-marker',
    html: `<span style="
      display:inline-block;width:16px;height:16px;border-radius:50%;
      background:${getStatusColor(normalizeStatus(bookingStatus))};
      box-shadow:0 0 0 2px #fff inset, 0 0 0 1px rgba(0,0,0,.2);
    "></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  })

// -------------------- MAP --------------------
const initializeMap = async () => {
  if (!mapContainer.value) return

  if (leafletMap.value) {
    leafletMap.value.remove()
    leafletMap.value = null
  }

  leafletMap.value = L.map(mapContainer.value, {
    zoomControl: true,
    maxZoom: 18,
    minZoom: 3,
    maxBounds: [
      [-90, -180],
      [90, 180],
    ],
    maxBoundsViscosity: 1.0,
  }).setView([40.4093, 49.8671], 11)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 18,
    minZoom: 3,
    attribution: '&copy; OpenStreetMap contributors',
    subdomains: 'abc',
  }).addTo(leafletMap.value as L.Map)

  areasLayerGroup.value = L.layerGroup().addTo(leafletMap.value as L.Map)
  markerClusterGroup.value = L.markerClusterGroup()
  leafletMap.value!.addLayer(markerClusterGroup.value as any)

  await nextTick()
  leafletMap.value.invalidateSize()
}

const updateMapMarkers = () => {
  if (!leafletMap.value || !markerClusterGroup.value) return

  markerClusterGroup.value.clearLayers()
  markers.value.clear()
  highlightedMarker.value?.remove()
  highlightedMarker.value = null

  const visible = getVisibleVehicles()
  if (!visible.length) return

  const bounds = L.latLngBounds([])
  let shouldCenterOnHighlighted = false
  let validMarkersCount = 0

  for (const v of visible) {
    const lat = Number(v.location.lat)
    const lng = Number(v.location.lng)

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      Math.abs(lat) > 90 ||
      Math.abs(lng) > 180
    ) {
      console.warn(
        `Invalid coordinates for vehicle ${v.id}: lat=${lat}, lng=${lng}`
      )
      continue
    }

    const isHighlighted = highlightedVehicleId.value === v.id

    const m = L.marker([lat, lng], {
      icon: isHighlighted
        ? createHighlightedMarkerIcon()
        : createMarkerIcon(v.booking_status || v.status),
    }).on('click', () => showVehicleDetails(v))

    markerClusterGroup.value.addLayer(m)
    validMarkersCount++

    if (isHighlighted) {
      highlightedMarker.value = m
      shouldCenterOnHighlighted = true
    } else {
      markers.value.set(v.id, m)
    }

    bounds.extend([lat, lng])
  }

  if (validMarkersCount > 0) {
    if (shouldCenterOnHighlighted && highlightedMarker.value) {
      leafletMap.value.setView(highlightedMarker.value.getLatLng(), 15)
    } else if (bounds.isValid()) {
      leafletMap.value.fitBounds(bounds, {
        padding: [24, 24],
        maxZoom: 16,
      })
    }
  }
}

// -------------------- API --------------------
const renderAreas = () => {
  if (!areasLayerGroup.value || !evAreas.value.length) return

  areasLayerGroup.value.clearLayers()

  evAreas.value.forEach(area => {
    if (!area.is_active) return // Show only active areas
    if (!area.points || area.points.length < 3) return

    const latLngs = area.points.map(p => [p.lat, p.lng] as [number, number])

    const polygon = L.polygon(latLngs, {
      color: '#409EFF',
      weight: 2,
      opacity: 0.6,
      fillColor: '#409EFF',
      fillOpacity: 0.15,
      interactive: false, // Make sure clicks pass through to the map if needed, or set true if we want tooltips
    })

    areasLayerGroup.value?.addLayer(polygon)
  })

  // Ensure areas are behind markers
  // Note: MarkerClusterGroup usually puts markers in the markerPane which is above overlayPane (where polygons are).
  // So polygons should naturally be below markers.
}

const loadEvAreas = async () => {
  try {
    const response = await getEvAreas()
    if (response?.data) {
      evAreas.value = response.data
      renderAreas()
    }
  } catch (e) {
    console.error('Failed to load EV areas', e)
  }
}

const loadEvCarsStats = async () => {
  try {
    const response: any = await getEvMapCars({ page: 1, perPage: 1 })
    // Check if response has data property (wrapper)
    if (response?.data?.meta?.counters) {
      stats.value = response.data.meta.counters
    } else if (response?.meta?.counters) {
      stats.value = response.meta.counters
    }
  } catch (e) {
    console.error('Failed to load EV cars stats', e)
  }
}

const loadEvCars = async () => {
  try {
    loading.value = true
    error.value = null

    // Load stats in parallel to get full counters
    loadEvCarsStats()

    let response: any
    try {
      const statusMap: Record<string, string> = {
        active: 'available',
        ongoing: 'ongoing',
        reserved: 'reserved',
        inactive: 'deactive',
        on_hold: 'on_hold',
      }
      const apiStatuses =
        selectedStatuses.value.length > 0
          ? selectedStatuses.value.map(s => statusMap[s] || s)
          : undefined

      response = await getEvMapCars({
        page: 1,
        perPage: 1000,
        status: apiStatuses,
      })

      if (response?.meta?.counters) {
        // We prioritize separate stats call, but fallback here if needed
        // stats.value = response.meta.counters
      }
    } catch {
      response = await getEvCarProfiles({ page: 1, perPage: 1000 })
    }

    let carsData: any[] = []
    if (response?.data) {
      if (Array.isArray(response.data?.data)) carsData = response.data.data
      else if (Array.isArray(response.data)) carsData = response.data
    } else if (Array.isArray(response)) {
      carsData = response
    }

    const mappedCars = (carsData || []).map((car: any) => ({
      ...car,
      status: normalizeStatus(car.status),
      booking_status: normalizeStatus(car.booking_status || car.status),
    }))

    evCars.value = Array.isArray(mappedCars) ? mappedCars : []

    lastUpdated.value = new Date()
    await nextTick()
    updateMapMarkers()
  } catch (e) {
    const error = e as any
    error.value =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load EV vehicles. Please try again.'
    evCars.value = []
  } finally {
    loading.value = false
  }
}

// -------------------- EVENTS --------------------
const refreshData = async () => {
  refreshing.value = true
  await Promise.all([loadEvCars(), loadEvAreas()])
  refreshing.value = false
}

const filterChanged = async () => {
  highlightedVehicleId.value = null
  await loadEvCars()
}

const showVehicleDetails = (vehicle: EvMapCar) => {
  selectedVehicle.value = vehicle
  showVehicleDialog.value = true
}

const viewVehicleDetails = () => {
  if (selectedVehicle.value) {
    router.push({
      name: routeNames.evVehicleDetails,
      params: { id: selectedVehicle.value.id },
    })
  }
}

// -------------------- LIFECYCLE --------------------
onMounted(async () => {
  if (isMounted.value) return
  isMounted.value = true

  await nextTick()
  await initializeMap()

  await Promise.all([loadEvCars(), loadEvAreas()])
})

onUnmounted(() => {
  markers.value.forEach(m => m.remove())
  markers.value.clear()
  areasLayerGroup.value?.clearLayers()
  areasLayerGroup.value = null
  leafletMap.value?.remove()
  leafletMap.value = null
})

return (_ctx: any,_cache: any) => {
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_checkbox_group = _resolveComponent("el-checkbox-group")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_image = _resolveComponent("el-image")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (error.value)
      ? (_openBlock(), _createBlock(CommonError, {
          key: 0,
          errorMessage: error.value,
          onRetryPress: loadEvCars
        }, null, 8, ["errorMessage"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[3] || (_cache[3] = _createElementVNode("h4", null, "Filter by Status:", -1)),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_el_checkbox_group, {
              modelValue: selectedStatuses.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedStatuses).value = $event)),
              onChange: filterChanged
            }, {
              default: _withCtx(() => [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(vehicleStatuses, (status) => {
                  return _createVNode(_component_el_checkbox, {
                    key: status.value,
                    label: status.value,
                    class: "status-checkbox",
                    style: _normalizeStyle({ '--status-color': status.color })
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(status.label) + " (" + _toDisplayString(getCountByStatus(status.value)) + ") ", 1)
                    ]),
                    _: 2
                  }, 1032, ["label", "style"])
                }), 64))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "stat-label" }, "Total Vehicles:", -1)),
            _createElementVNode("span", _hoisted_8, _toDisplayString(totalVehiclesCount.value), 1)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "stat-label" }, "Visible:", -1)),
            _createElementVNode("span", _hoisted_10, _toDisplayString(filteredEvCars.value?.length || 0), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createVNode(_component_el_button, {
            type: "primary",
            icon: "el-icon-refresh",
            onClick: refreshData,
            loading: refreshing.value
          }, {
            default: _withCtx(() => _cache[6] || (_cache[6] = [
              _createTextVNode(" Refresh ")
            ])),
            _: 1,
            __: [6]
          }, 8, ["loading"])
        ])
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", {
          ref_key: "mapContainer",
          ref: mapContainer,
          id: "ev-map-container",
          class: "leaflet-map"
        }, null, 512),
        (loading.value && !(evCars.value?.length || 0))
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _cache[7] || (_cache[7] = _createElementVNode("p", null, "Loading map...", -1)),
              (error.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(error.value), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (!leafletMap.value && !loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, _cache[8] || (_cache[8] = [
              _createElementVNode("p", null, "Initializing map...", -1)
            ])))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_component_el_dialog, {
        modelValue: showVehicleDialog.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showVehicleDialog).value = $event)),
        title: `Vehicle Details - ${selectedVehicle.value?.external_id}`,
        width: "600px"
      }, {
        footer: _withCtx(() => [
          _createVNode(_component_el_button, {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (showVehicleDialog.value = false))
          }, {
            default: _withCtx(() => _cache[19] || (_cache[19] = [
              _createTextVNode("Close")
            ])),
            _: 1,
            __: [19]
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: viewVehicleDetails
          }, {
            default: _withCtx(() => _cache[20] || (_cache[20] = [
              _createTextVNode(" View Full Details ")
            ])),
            _: 1,
            __: [20]
          })
        ]),
        default: _withCtx(() => [
          (selectedVehicle.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                _createVNode(_component_el_row, { gutter: 20 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_17, [
                          _cache[9] || (_cache[9] = _createElementVNode("strong", null, "Make:", -1)),
                          _createTextVNode(" " + _toDisplayString(selectedVehicle.value?.model_make_id), 1)
                        ]),
                        _createElementVNode("div", _hoisted_18, [
                          _cache[10] || (_cache[10] = _createElementVNode("strong", null, "Model:", -1)),
                          _createTextVNode(" " + _toDisplayString(selectedVehicle.value?.model_name), 1)
                        ]),
                        _createElementVNode("div", _hoisted_19, [
                          _cache[11] || (_cache[11] = _createElementVNode("strong", null, "External ID:", -1)),
                          _createTextVNode(" " + _toDisplayString(selectedVehicle.value?.external_id), 1)
                        ]),
                        _createElementVNode("div", _hoisted_20, [
                          _cache[12] || (_cache[12] = _createElementVNode("strong", null, "Status:", -1)),
                          _createVNode(_component_el_tag, {
                            type: 
                    getStatusType(
                      selectedVehicle.value.booking_status || selectedVehicle.value?.status
                    )
                  ,
                            size: "small"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(getStatusLabel(
                      selectedVehicle.value?.booking_status || selectedVehicle.value?.status
                    )), 1)
                            ]),
                            _: 1
                          }, 8, ["type"])
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_21, [
                          _cache[13] || (_cache[13] = _createElementVNode("strong", null, "Battery Level:", -1)),
                          _createTextVNode(" " + _toDisplayString(selectedVehicle.value?.electric_level || 'N/A') + "% ", 1)
                        ]),
                        _createElementVNode("div", _hoisted_22, [
                          _cache[14] || (_cache[14] = _createElementVNode("strong", null, "Range:", -1)),
                          _createTextVNode(" " + _toDisplayString(selectedVehicle.value?.range_per_charge || 'N/A') + " km ", 1)
                        ]),
                        _createElementVNode("div", _hoisted_23, [
                          _cache[15] || (_cache[15] = _createElementVNode("strong", null, "Last Updated:", -1)),
                          _createTextVNode(" " + _toDisplayString(formatTime(new Date(selectedVehicle.value?.updated_at))), 1)
                        ]),
                        _createElementVNode("div", _hoisted_24, [
                          _cache[16] || (_cache[16] = _createElementVNode("strong", null, "Favorite:", -1)),
                          _createVNode(_component_el_tag, {
                            type: selectedVehicle.value?.favorite ? 'success' : 'info',
                            size: "small"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(selectedVehicle.value?.favorite ? 'Yes' : 'No'), 1)
                            ]),
                            _: 1
                          }, 8, ["type"])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (
              selectedVehicle.value?.images &&
                Array.isArray(selectedVehicle.value.images) &&
                selectedVehicle.value.images.length > 0
            )
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                      _cache[18] || (_cache[18] = _createElementVNode("h5", null, "Vehicle Images:", -1)),
                      _createElementVNode("div", _hoisted_26, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((selectedVehicle.value?.images || []).slice(0, 4), (image) => {
                          return (_openBlock(), _createBlock(_component_el_image, {
                            key: image.id,
                            src: image.secure_url,
                            "preview-src-list": 
                  (selectedVehicle.value?.images || []).map((img) => img.secure_url)
                ,
                            fit: "cover",
                            class: "vehicle-image"
                          }, {
                            error: _withCtx(() => [
                              _createElementVNode("div", _hoisted_27, [
                                _createVNode(_component_el_icon, { class: "image-slot-icon" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(PictureFilled))
                                  ]),
                                  _: 1
                                }),
                                _cache[17] || (_cache[17] = _createElementVNode("span", { class: "image-slot-text" }, "Image not available", -1))
                              ])
                            ]),
                            _: 2
                          }, 1032, ["src", "preview-src-list"]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "title"])
    ])
  ]))
}
}

})